import React, {
  FC, FormEvent, useEffect, useRef, useState
} from 'react';
import {
  useNavigate, useSearchParams
} from 'react-router-dom';
import {
  Controller, useForm
} from 'react-hook-form';
import {
  zodResolver
} from '@hookform/resolvers/zod';
import {
  useTranslation
} from 'react-i18next';

import {
  useTypedDispatch, useTypedSelector
} from 'src/redux';
import {
  AcceptTermsBlock,
  Input,
  InputType,
  warningToast,
  successfulToast,
  Button,
  Spinner,
} from 'src/shared/components';
import {
  userSelectors
} from 'src/redux/user';
import {
  ROUTE,
  isErrorWithMessage,
  verification,
  showWarningFromServer,
  themeColors,
  initVerification,
  getOrgSubdomain,
} from 'src/shared/utils';
import {
  usePostAuthSignInMutation,
  usePostReverificationsResendMutation,
  usePostUserVerificationsMutation,
  usePostSignUpMutation,
  useGetGroupsUsersInviteAcceptSessionQuery,
} from 'src/redux/openapi';
import {
  useGetInputValidation, useGetRemainingTime
} from 'src/shared/hooks';
import {
  ToastBlockContainer
} from 'src/shared/components/ToastBlockContainer';
import {
  useGetLoginExistsMutation
} from 'src/shared/api/auth/authApi';
import {
  setEmailToVerify
} from 'src/redux/verification';

import {
  InputNames
} from '../types';

import {
  SubmitButton
} from './SubmitButton';
import * as Style from './Login.styles';
import {
  GroupInviteLabel
} from './GroupInviteLabel';

interface EnterPasswordPageProps {
  isSocials?: boolean;
}

interface LoginForm {
  [InputNames.EMAIL]: string;
  [InputNames.PASSWORD]: string;
}

interface InviteRegForm {
  [InputNames.EMAIL]: string;
  [InputNames.USERNAME]: string;
  [InputNames.FULL_NAME]: string;
  [InputNames.PASSWORD]: string;
  [InputNames.AGREEMENT]: boolean;
}

const formId = 'EnterPasswordForm';
const inviteFormId = 'InviteRegForm';

export const EnterPasswordPage: FC<EnterPasswordPageProps> = ({
  isSocials,
}) => {
  const isUninitialized = useRef(false);

  const [isReverificationRequired, setIsReverificationRequired] = useState(false);

  const {
    startCountdown, remainingTime
  } = useGetRemainingTime();

  const navigate = useNavigate();

  const dispatch = useTypedDispatch();

  const {
    t
  } = useTranslation();

  const [login, {
    isLoading
  }] = usePostAuthSignInMutation();

  const {
    enterPasswordPageSchema, createAccountSchema
  } = useGetInputValidation();

  const [searchParams] = useSearchParams();
  const isReverification = searchParams.get(verification) === 'true';
  const isInitVerification = searchParams.get(initVerification);

  const [verifyAccount] = usePostUserVerificationsMutation();

  const onAccountVerification = async () => {
    try {
      const response = await verifyAccount().unwrap();

      successfulToast(response.message);
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  useEffect(
    () => {
      if (isUninitialized.current === false) {
        if (isInitVerification === 'true') {
          onAccountVerification();
        }

        isUninitialized.current = true;
      }
    },
    []
  );

  const [resendVerification, {
    isLoading: isResendLoading
  }] = usePostReverificationsResendMutation();

  const [getLoginExists] = useGetLoginExistsMutation();

  const onResend = async () => {
    try {
      const response = await resendVerification().unwrap();

      startCountdown();

      successfulToast(response.message);
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  const {
    email: userEmail,
    id: userId,
    name,
  } = useTypedSelector(userSelectors.user);

  const {
    handleSubmit,
    setValue,
    getValues,
    control,
    setError,
    formState: {
      errors
    },
  } = useForm<LoginForm>({
    mode: 'onTouched',
    defaultValues: {
      [InputNames.EMAIL]: userEmail || name || '',
      [InputNames.PASSWORD]: '',
    },
    resolver: zodResolver(enterPasswordPageSchema),
  });

  const {
    data: inviteSessionData
  } = useGetGroupsUsersInviteAcceptSessionQuery();

  const hasInviteSession = !!inviteSessionData?.invitationSession
    && !!inviteSessionData.data
    && !!getOrgSubdomain();

  const invitedEmail = inviteSessionData?.data?.userEmail || '';

  const {
    handleSubmit: handleInviteSubmit,
    control: inviteControl,
    formState: {
      errors: inviteErrors
    },
  } = useForm<InviteRegForm>({
    mode: 'onTouched',
    defaultValues: {
      [InputNames.EMAIL]: invitedEmail,
      [InputNames.USERNAME]: '',
      [InputNames.FULL_NAME]: '',
      [InputNames.PASSWORD]: '',
      [InputNames.AGREEMENT]: false,
    },
    resolver: zodResolver(createAccountSchema),
  });

  const [signup, {
    isLoading: isSignupLoading
  }] = usePostSignUpMutation();

  const onInviteSubmit = handleInviteSubmit(
    async ({
      email, username, fullName, password, agreement
    }) => {
      try {
        const response = await signup({
          signUpBodySchema: {
            email: email.trim(),
            password,
            username: username.trim(),
            fullName: fullName.trim(),
            agreement,
          },
        }).unwrap();

        successfulToast(response.message);

        const {
          verificationRequired
        } = response.data;

        if (verificationRequired) {
          dispatch(setEmailToVerify(verificationRequired.email));

          navigate(`/${ROUTE.EMAIL_VERIFICATION}`);

          return;
        }

        navigate(`/${ROUTE.QUESTION}`);
      } catch (error) {
        showWarningFromServer(error);
      }
    },
  );

  const onSubmit = handleSubmit(async ({
    email, password
  }) => {
    if (!!email.trim() && password) {
      try {
        const response = await login({
          signInBodySchema: {
            login: email.trim(),
            password,
          },
        }).unwrap();

        const {
          reverificationRequired,
          verificationRequired,
          userBlocked,
          isGroupAssigned,
        } = response.data;

        successfulToast(response.message);

        if (reverificationRequired) {
          return setIsReverificationRequired(true);
        }

        if (verificationRequired) {
          dispatch(setEmailToVerify(verificationRequired.email));

          return navigate(`/${ROUTE.EMAIL_VERIFICATION}`);
        }

        if (userBlocked) {
          return navigate(`/${ROUTE.ACCOUNT_BANNED}`);
        }

        if (!isGroupAssigned) {
          return navigate(`/${ROUTE.CREATE_GROUP}`);
        }

        return navigate(`/${ROUTE.QUESTION}`);
      } catch (error) {
        if (isErrorWithMessage(error)) {
          warningToast(error.data.message);

          // TODO: change with codes after BE implementation
          if (error.data.message === t('errors.userNotFound')) {
            setError(
              InputNames.EMAIL,
              {
                type: 'manual',
                message: t('errors.userNotFound'),
              }
            );
          }

          if (error.data.message === t('errors.invalidPassword')) {
            setError(
              InputNames.PASSWORD,
              {
                type: 'manual',
                message: t('errors.invalidPassword'),
              }
            );
          }
        }
      }
    }

    return null;
  });

  useEffect(
    () => {
      if (userId) {
        navigate(`/${ROUTE.QUESTION}`);
      }
    },
    [userId]
  );

  const handleSubmitClick = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };

  const onBlurEmail = async () => {
    const {
      email
    } = getValues();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setValue(
        InputNames.EMAIL,
        ''
      );

      return;
    }

    try {
      const response = await getLoginExists(trimmedEmail).unwrap();

      if (!response.data.userExists) {
        setError(
          InputNames.EMAIL,
          {
            type: 'manual',
            message: t('errors.userNotFound'),
          }
        );
      }
    } catch (error) {
      showWarningFromServer(error);
    }
  };

  if (hasInviteSession && !isSocials) {
    return (
      <Style.MainContainer>
        <Style.Title>{t('login.createAccountToJoin')}</Style.Title>

        <Style.SocialsContainer>
          <GroupInviteLabel />
        </Style.SocialsContainer>

        <Style.Form
          id={inviteFormId}
          onSubmit={(e) => {
            e.preventDefault();
            onInviteSubmit();
          }}
        >
          <Controller
            name={InputNames.EMAIL}
            control={inviteControl}
            render={({
              field
            }) => (
              <Input
                label={t('common.email')}
                type={InputType.EMAIL}
                isRequired
                value={field.value}
                onChange={(e) => field.onChange(e)}
                errorMessage={inviteErrors[InputNames.EMAIL]?.message}
                onBlur={field.onBlur}
                disabled={!!invitedEmail}
              />
            )}
          />

          <Controller
            name={InputNames.USERNAME}
            control={inviteControl}
            render={({
              field
            }) => (
              <Input
                label={t('common.username')}
                isRequired
                value={field.value}
                onChange={(e) => field.onChange(e)}
                errorMessage={inviteErrors[InputNames.USERNAME]?.message}
                bottomMessage={t('login.usernameBottomMsg')}
                onBlur={field.onBlur}
              />
            )}
          />

          <Controller
            name={InputNames.FULL_NAME}
            control={inviteControl}
            render={({
              field
            }) => (
              <Input
                label={t('common.fullName')}
                isRequired
                value={field.value}
                onChange={(e) => field.onChange(e)}
                errorMessage={inviteErrors[InputNames.FULL_NAME]?.message}
                onBlur={field.onBlur}
                placeholder={t('common.fullNamePlaceholder')}
              />
            )}
          />

          <Controller
            name={InputNames.PASSWORD}
            control={inviteControl}
            render={({
              field
            }) => (
              <Input
                label={t('common.password')}
                isRequired
                value={field.value}
                type={InputType.PASSWORD}
                onChange={(e) => field.onChange(e)}
                errorMessage={inviteErrors[InputNames.PASSWORD]?.message}
                onBlur={field.onBlur}
                placeholder={t('login.passwordPlaceholder')}
              />
            )}
          />
        </Style.Form>

        <Controller
          name={InputNames.AGREEMENT}
          control={inviteControl}
          render={({
            field
          }) => (
            <AcceptTermsBlock
              termsAccepted={field.value || false}
              handleTermsAccept={() => field.onChange(!field.value)}
              errorMessage={inviteErrors[InputNames.AGREEMENT]?.message}
            />
          )}
        />

        <Style.ButtonsContainer>
          <SubmitButton
            title={t('buttons.finish')}
            onClick={onInviteSubmit}
            isLoading={isSignupLoading}
            formId={inviteFormId}
          />

          <Button
            variant="big-black"
            className="font-bold text-17-26 md:text-xl text-white"
            onClick={() => navigate(`/${ROUTE.SOCIAL_LOGIN}`)}
          >
            {t('buttons.returnToLogin')}
          </Button>
        </Style.ButtonsContainer>
      </Style.MainContainer>
    );
  }

  return (
    <Style.MainContainer>
      {!getOrgSubdomain() && <Style.Title>{t('login.title')}</Style.Title>}

      {isSocials && (
        <Style.SocialsContainer>
          <GroupInviteLabel />
        </Style.SocialsContainer>
      )}

      {isReverificationRequired && (
        <ToastBlockContainer
          isWarning
          text={t('verification.accountIsNotVerified')}
          fullWidth
        />
      )}

      {isReverification && (
        <ToastBlockContainer
          isWarning
          text={t('verification.loginToComplete')}
          fullWidth
        />
      )}

      <Style.Form
        onSubmit={handleSubmitClick}
        id={formId}
      >
        <Controller
          name={InputNames.EMAIL}
          control={control}
          render={({
            field
          }) => {
            const onChange = (value: string) => {
              if (isReverificationRequired) {
                setIsReverificationRequired(false);
              }

              field.onChange(value);
            };

            const onBlur = () => {
              field.onBlur();
              onBlurEmail();
            };

            return (
              <Input
                label={t('login.labelLogin')}
                value={field.value}
                onChange={onChange}
                errorMessage={errors[InputNames.EMAIL]?.message}
                onBlur={onBlur}
              />
            );
          }}
        />

        <Controller
          name={InputNames.PASSWORD}
          control={control}
          render={({
            field
          }) => {
            return (
              <Input
                label={t('common.password')}
                value={field.value}
                onChange={(e) => field.onChange(e)}
                type={InputType.PASSWORD}
                errorMessage={errors[InputNames.PASSWORD]?.message}
                onBlur={field.onBlur}
                placeholder={t('login.passwordPlaceholder')}
              />
            );
          }}
        />
      </Style.Form>

      <Style.ButtonsContainer>
        <SubmitButton
          title={t('common.logIn')}
          isLoading={isLoading}
          isDisabled={
            !!errors[InputNames.EMAIL] || !!errors[InputNames.PASSWORD]
          }
          onClick={onSubmit}
          formId="EnterPasswordForm"
        />

        {isReverificationRequired && (
          <Button
            variant="big-grey-bordered"
            className="font-bold text-17-26 md:text-xl text-dark-gray"
            onClick={onResend}
            disabled={!!remainingTime || isResendLoading}
          >
            {remainingTime ? (
              `${t('recoveryPage.wait')} ${remainingTime}`
            ) : (
              <>
                {t('verification.resendEmail')}

                {isResendLoading && (
                  <Spinner
                    size={24}
                    color={themeColors['dark-gray']}
                    centered={false}
                  />
                )}
              </>
            )}
          </Button>
        )}
      </Style.ButtonsContainer>
    </Style.MainContainer>
  );
};
